// Editor command registry (PERF-001 / audit F1b). These list-indent and mobile block-selection
// commands operate on a mounted editor, but App wires them into its GLOBAL keyboard + block-action-bar
// handlers (which live in the always-eager shell). Importing them straight from the `@lapo/ui` barrel
// dragged editorBehavior.ts / blockSelectionPlugin.ts — and thus the whole @tiptap / prosemirror-view
// runtime (~73KB gz) — into the eager login bundle, defeating the F1b split (editorRuntime already
// deep-imports for the same reason).
//
// Instead App calls these through this LIGHT module (the only @tiptap dependency is a `import type`,
// fully erased at build — no runtime edge), and the lazy editor chunk (editors.tsx) fills in the real
// implementations when it loads via registerEditorCommands(). Every App call site guards on a mounted
// editor (activeEditorRef != null / an editor handed to onBlockSelection), and an editor can only exist
// after editors.tsx has loaded and registered — so the registry is always populated by the time it's
// read. Mirrors editorRuntime.setAuthTokenProvider.
// `EditorHandle` (= the tiptap Editor createEditor returns) as a type-only import — fully erased at
// build, so this module stays free of any @tiptap runtime edge. `@lapo/ui/files` is the light,
// editor-free upload module (same one editorRuntime.ts uses).
import type { EditorHandle } from './editors.js';
import type { UploadFn } from '@lapo/ui/files';

type Editor = EditorHandle;

export interface EditorCommands {
  indentListItem: (editor: Editor) => boolean;
  outdentListItem: (editor: Editor) => boolean;
  toggleNumbered: (editor: Editor) => boolean;
  clearBlockSelection: (editor: Editor) => void;
  indentBlockSelection: (editor: Editor) => boolean;
  outdentBlockSelection: (editor: Editor) => boolean;
  deleteBlockSelection: (editor: Editor) => boolean;
  blockSelectionAllNumbered: (editor: Editor) => boolean;
  copyBlockSelection: (editor: Editor) => Promise<boolean>;
  moveBlockSelection: (editor: Editor, dir: 1 | -1) => boolean;
  canMoveBlockSelection: (editor: Editor, dir: 1 | -1) => boolean;
  uploadAndInsert: (editor: Editor, upload: UploadFn, container: HTMLElement, files: File[], pos?: number) => void;
}

let commands: EditorCommands | null = null;

/** Called once by the lazy editor chunk (editors.tsx) on load, before any editor can mount. */
export function registerEditorCommands(impls: EditorCommands): void {
  commands = impls;
}

function required(): EditorCommands {
  // Unreachable in practice: every caller already holds a mounted editor, which implies the editor
  // chunk loaded and registered. The throw makes a wiring regression loud rather than a silent no-op.
  if (!commands) throw new Error('editor commands used before the editor chunk registered them');
  return commands;
}

export const indentListItem: EditorCommands['indentListItem'] = (e) => required().indentListItem(e);
export const outdentListItem: EditorCommands['outdentListItem'] = (e) => required().outdentListItem(e);
export const toggleNumbered: EditorCommands['toggleNumbered'] = (e) => required().toggleNumbered(e);
export const clearBlockSelection: EditorCommands['clearBlockSelection'] = (e) => required().clearBlockSelection(e);
export const indentBlockSelection: EditorCommands['indentBlockSelection'] = (e) => required().indentBlockSelection(e);
export const outdentBlockSelection: EditorCommands['outdentBlockSelection'] = (e) => required().outdentBlockSelection(e);
export const deleteBlockSelection: EditorCommands['deleteBlockSelection'] = (e) => required().deleteBlockSelection(e);
export const blockSelectionAllNumbered: EditorCommands['blockSelectionAllNumbered'] = (e) => required().blockSelectionAllNumbered(e);
export const copyBlockSelection: EditorCommands['copyBlockSelection'] = (e) => required().copyBlockSelection(e);
export const moveBlockSelection: EditorCommands['moveBlockSelection'] = (e, dir) => required().moveBlockSelection(e, dir);
export const canMoveBlockSelection: EditorCommands['canMoveBlockSelection'] = (e, dir) => required().canMoveBlockSelection(e, dir);
export const uploadAndInsert: EditorCommands['uploadAndInsert'] = (e, upload, container, files, pos) =>
  required().uploadAndInsert(e, upload, container, files, pos);
